// Ticket D: PWA Install Banner Component
// Fixed: Only shows on public pages, respects dismiss, safe spacing
import React, { useEffect, useState } from "react";

/**
 * InstallAppBanner
 * - Only show when:
 *   - user is NOT logged in (public pages), OR you explicitly allow it
 *   - AND browser fired beforeinstallprompt
 * - Dismissable + remembered in localStorage
 * - Positioned above safe-area so it doesn't block UI
 */
export default function InstallAppBanner({ isLoggedIn = false, forceShow = false }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("pwa_install_dismissed");
    if (dismissed === "1") setHidden(true);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Don't show inside the logged-in app (this prevents blocking logged-in UI)
  const shouldShow = (forceShow || !isLoggedIn) && !!deferredPrompt && !hidden;

  if (!shouldShow) return null;

  const onInstall = async () => {
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // If they accept or dismiss, don't nag them immediately.
      localStorage.setItem("pwa_install_dismissed", "1");
      setHidden(true);
      setDeferredPrompt(null);
      // outcome is "accepted" or "dismissed" - no console spam
    } catch {
      localStorage.setItem("pwa_install_dismissed", "1");
      setHidden(true);
    }
  };

  const onClose = () => {
    localStorage.setItem("pwa_install_dismissed", "1");
    setHidden(true);
  };

  return (
    <div
      className="fixed left-3 right-3 z-[9998] rounded-2xl shadow-lg"
      style={{
        // sits ABOVE the Emergent badge and above bottom safe-area, but not covering main UI controls
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
      }}
      role="region"
      aria-label="Install Unity Love banner"
    >
      <div className="flex items-center justify-between gap-3 rounded-2xl bg-pink-500 px-4 py-3 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-white/15 grid place-items-center shrink-0">
            <span aria-hidden="true">⬇️</span>
          </div>
          <div className="min-w-0">
            <div className="font-semibold leading-tight truncate">Install Unity Love</div>
            <div className="text-xs opacity-90 leading-tight truncate">
              Faster access &amp; push notifications
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onInstall}
            className="rounded-full bg-white text-pink-600 px-4 py-2 text-sm font-semibold"
          >
            Install
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white/15 px-3 py-2 text-sm font-semibold"
            aria-label="Dismiss install banner"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
